import React, { ComponentType, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import HomeOutlined from '@mui/icons-material/HomeOutlined';

import { PageWrapper as UntypedPageWrapper, DMSALogo, useAuth, useMatomo } from '@omnigenbiodata/ui';

import clsx from 'classnames';

import { useStyles } from './component.styles';
import { PARTNER_LOGOS } from '../../core/constants/content.constant';
import SideMenu from '../../components/navigation/SideMenu';
import PartnerLogoStrip from '../../components/content/PartnerLogoStrip';
import ROUTES from '../../core/constants/routes.constant';
import { healthDataOptInSelector } from '../../store/participant/selectors';
import { useAppSelector } from '../../store';

interface PortalLayoutProps {
  children: ReactNode;
}

interface PageWrapperFixedProps {
  children: ReactNode;
  isShaded?: boolean;
}

const PageWrapper = UntypedPageWrapper as ComponentType<PageWrapperFixedProps>;

function PortalLayout({ children }: PortalLayoutProps) {
  const { trackEvent } = useMatomo();
  const { t } = useTranslation('portal');
  const { classes } = useStyles();
  const { isAuthenticated } = useAuth();

  const [isMenuOpenFlag, setIsMenuOpenFlag] = useState(false);

  const healthDataOptIn = useAppSelector(healthDataOptInSelector);

  const handleToggleMenu = (open: boolean) => (event: any) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setIsMenuOpenFlag(open);

    if (open) {
      trackEvent({ category: 'nav', action: 'open-side-menu' });
    }
  };

  return (
    <>
      {isAuthenticated && (
        <SideMenu isOpen={isMenuOpenFlag} onToggle={handleToggleMenu} showHealthOption={healthDataOptIn} />
      )}

      <PageWrapper>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            component="header"
            className={clsx(classes.header, {
              [classes.hasMenu]: isAuthenticated,
            })}
            sx={{
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              position: 'relative',
              zIndex: 10,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
              '&:after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 4,
                background: 'linear-gradient(90deg, #F2AE21 0%, #5ABBE5 24%, #92C15F 48%, #12ACA2 72%, #EA516D 100%)',
              },
            }}
            mb={2}
          >
            <Box
              sx={{
                maxWidth: 1180,
                mx: 'auto',
                px: { xs: 2, md: 3 },
                py: { xs: 1.5, md: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                minHeight: 82,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, md: 4 },
                  minWidth: 0,
                }}
              >
                <Link to={ROUTES.portal} className={classes.logo}>
                  <DMSALogo size="sm" />
                </Link>

                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    color="text.secondary"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: '0.12em',
                      lineHeight: 1,
                    }}
                  >
                    Participant portal
                  </Typography>

                  <Typography
                    variant="h6"
                    component="p"
                    sx={{
                      mt: 0.5,
                      color: 'text.primary',
                      fontWeight: 900,
                      lineHeight: 1.15,
                    }}
                  >
                    Discover Me South Africa
                  </Typography>
                </Box>
              </Box>

              {isAuthenticated && (
                <Button
                  component={Link}
                  to={ROUTES.portal}
                  variant="contained"
                  startIcon={<HomeOutlined />}
                  sx={{
                    borderRadius: 999,
                    px: { xs: 1.75, sm: 2.5 },
                    py: 1,
                    bgcolor: '#123E57',
                    color: 'common.white',
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: '0 8px 18px rgba(18, 62, 87, 0.22)',
                    '&:hover': {
                      bgcolor: '#0E3145',
                      boxShadow: '0 10px 24px rgba(18, 62, 87, 0.28)',
                    },
                  }}
                >
                  {t('home.homeLinkShort')}
                </Button>
              )}
            </Box>
          </Box>

          <Fade in timeout={500}>
            <main
              className={classes.content}
              style={{
                flex: 1,
                width: '100%',
              }}
            >
              {children}
            </main>
          </Fade>

          <div className={classes.footer}>
            <PartnerLogoStrip logos={PARTNER_LOGOS} title="Study partners" />
          </div>
        </div>
      </PageWrapper>
    </>
  );
}

export default PortalLayout;
